'use client';

import SvgCheck from '@common/__generated__/icon/components/SvgCheck';
import SvgChevrondown from '@common/__generated__/icon/components/SvgChevrondown';
import { Checkbox } from '@common/components/atom/Checkbox/Checkbox';
import type { InputProps } from '@common/components/atom/Input/Input';
import { Input } from '@common/components/atom/Input/Input';
import { cn } from '@common/utils';
import * as RadixDropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useLayoutEffect, useRef, useState } from 'react';

import type { DropdownMenuType } from './type';

/**
 * * 드롭다운(셀렉트박스) 목록 요소 타입
 */
interface DropdownMenuItem<T extends string | number> {
  label: string;
  value: T;
}

/**
 * * 드롭다운(셀렉트박스) 컴포넌트 타입
 */
interface DropdownMenuProps<
  T extends string | number,
  M extends boolean = false,
  Type extends DropdownMenuType = 'basic',
> extends Omit<
    InputProps,
    | 'rightContent'
    | 'type'
    | 'leftContent'
    | 'onClear'
    | 'onChange'
    | 'value'
    | 'onFocus'
    | 'onBlur'
    | 'onInvalid'
  > {
  /** 드롭다운 메뉴 아이템 목록 */
  options: DropdownMenuItem<T>[];
  /** 열림/닫힌 상태 외부 제어용 */
  open?: boolean;
  /** 열림/닫힌 상태 외부 제어용 */
  onOpenChange?: (opened: boolean) => void;
  /** 다중 선택 여부 */
  multiple?: M;
  /** 선택 값 변경 핸들러 */
  onChange?: (value: M extends true ? T[] : T) => void;
  /** 선택 값 */
  value?: M extends true ? T[] : T;
  /** 드롭다운 트리거 타입 */
  type?: Type;
  /** 트리거 크기 설정 (type이 'text'인 경우 사용 불가) */
  dimensions?: Type extends 'text' ? never : InputProps['dimensions'];
}

/**
 * * 드롭다운(셀렉트박스) DS 컴포넌트
 * @summary 디자인 시스템의 드롭다운 컴포넌트입니다.
 * @description DS의 label 및 error, validation message 등등에 대한 처리는 InputDecorator 컴포넌트로 감싸서 사용해주세요
 */
export const DropdownMenu = <
  T extends string | number,
  M extends boolean = false,
  Type extends DropdownMenuType = 'basic',
>({
  options = [],
  value,
  placeholder,
  onChange,
  invalid,
  disabled,
  dimensions,
  open,
  onOpenChange,
  multiple,
  className,
  type = 'basic' as Type,
}: DropdownMenuProps<T, M, Type>) => {
  //* 내부 드롭다운 값
  const [builtInValue, setBuiltInValue] = useState<M extends true ? T[] : T>();
  //* 내부 드롭다운 열림/닫힘 상태
  const [builtInOpen, setBuiltInOpen] = useState(open ?? false);
  //* 트리거 element
  const triggerRef = useRef<HTMLButtonElement>(null);
  //* 트리거 너비
  const [triggerWidth, setTriggerWidth] = useState(0);

  //* 트리거 영역에 보여지는 실제 값
  const usingValue = value ?? builtInValue;
  const displayValue = Array.isArray(usingValue)
    ? usingValue
        .map((v) => options.find((opt) => opt.value === v)?.label ?? v)
        .join(', ')
    : (options.find((opt) => opt.value === usingValue)?.label ?? usingValue);

  const handleClickItem = (clickedValue: T) => {
    if (multiple) {
      const currentValues = (
        Array.isArray(value)
          ? value
          : Array.isArray(builtInValue)
            ? builtInValue
            : []
      ) as M extends true ? T[] : never;

      const newValue = currentValues.includes(clickedValue)
        ? currentValues.filter((v) => v !== clickedValue)
        : [...currentValues, clickedValue];

      setBuiltInValue(newValue as M extends true ? T[] : T);
      onChange?.(newValue as M extends true ? T[] : T);
    } else {
      setBuiltInValue(clickedValue as M extends true ? T[] : T);
      onChange?.(clickedValue as M extends true ? T[] : T);
    }
  };

  useLayoutEffect(() => {
    setTriggerWidth(triggerRef.current?.offsetWidth ?? 0);
  }, [displayValue]);

  //* 실제 열림/닫힘 상태
  const usingOpen = open ?? builtInOpen;

  return (
    <RadixDropdownMenuPrimitive.Root
      open={usingOpen}
      onOpenChange={(isOpen) => {
        onOpenChange?.(isOpen);
        setBuiltInOpen(isOpen);
      }}
    >
      <RadixDropdownMenuPrimitive.Trigger
        ref={triggerRef}
        className={cn(
          'relative select-none overflow-hidden outline-none',
          'min-w-150 max-w-[480px]',
          'mobile:min-w-130 mobile:max-w-335',
          {
            'w-fit min-w-[unset] mobile:w-fit mobile:min-w-[unset]':
              type === 'text',
          },
          className,
        )}
        disabled={disabled}
      >
        {type === 'basic' ? (
          <Input
            rightContent={
              <SvgChevrondown
                className={cn('size-16 text-Gray-600', {
                  'size-20': dimensions === 'H56',
                  'text-Gray-500': disabled,
                })}
              />
            }
            value={displayValue}
            placeholder={placeholder}
            className={cn({ 'w-full border-1 border-Gray-900': usingOpen })}
            invalid={invalid}
            type="button"
            disabled={disabled}
            dimensions={dimensions}
          />
        ) : (
          <div className="flex items-center justify-between gap-4 overflow-hidden py-10 pl-4 pr-2">
            <p
              className={cn(
                'flex-1 text-left font-400 text-Gray-900 typo-body4',
                'truncate',
                {
                  'text-Gray-400': disabled,
                },
              )}
            >
              {displayValue || placeholder}
            </p>
            <SvgChevrondown
              className={cn('size-12 text-Gray-900 duration-300', {
                '-rotate-180 text-Gray-600': usingOpen,
                'text-Gray-600': displayValue,
                'text-Gray-400': disabled,
              })}
            />
          </div>
        )}
      </RadixDropdownMenuPrimitive.Trigger>
      <RadixDropdownMenuPrimitive.Portal>
        <RadixDropdownMenuPrimitive.Content
          side="bottom"
          align="start"
          className="mobile:min-w-130 min-w-150 rounded-small bg-Gray-white py-12 shadow-xl"
          sideOffset={8}
          avoidCollisions={false}
          sticky="always"
          loop={false}
          style={{
            width: triggerWidth,
            minWidth: type === 'text' ? 'fit-content' : undefined,
          }}
        >
          {options?.map((item, index) => {
            const isSelected = Array.isArray(usingValue)
              ? usingValue.includes(item.value)
              : usingValue === item.value;

            return (
              <RadixDropdownMenuPrimitive.Item
                key={index}
                className="w-full outline-none"
              >
                <button
                  className="flex size-full select-none items-center justify-between gap-8 overflow-hidden px-16 py-8 outline-none duration-150 hover:bg-B-50"
                  onClick={(e) => {
                    if (multiple) {
                      e.preventDefault();
                    }

                    handleClickItem(item.value);
                  }}
                >
                  <>
                    <div className="pointer-events-none flex items-center gap-4">
                      {multiple ? <Checkbox checked={isSelected} /> : null}
                      <p className="line-clamp-1 flex-1 text-left font-400 text-Gray-800 typo-body4">
                        {item.label}
                      </p>
                    </div>
                    {!multiple && isSelected ? (
                      <SvgCheck className="size-16 text-B-500" />
                    ) : null}
                  </>
                </button>
              </RadixDropdownMenuPrimitive.Item>
            );
          })}
        </RadixDropdownMenuPrimitive.Content>
      </RadixDropdownMenuPrimitive.Portal>
    </RadixDropdownMenuPrimitive.Root>
  );
};
